import { TMS } from 'types/enums/Integrations';
import { titleCase } from 'utils/formatStrings';

/**
 * Base field label mappings common across all TMS forms.
 * Maps field names to human-readable labels for error messages.
 */
export const baseFieldLabelMap: Record<string, string> = {
  externalTMSID: 'Name',
  refNumber: 'Ref #',
  poNums: 'PO #',
  zipCode: 'Zip Code',
  readyTime: 'Pickup Date',
  readyTimeEnd: 'Latest Pickup Time',
  readyEndTime: 'Latest Pickup Time',
  mustDeliver: 'Delivery Date',
  mustDeliverEnd: 'Latest Delivery Time',
  apptStartTime: 'Appointment Start Time',
  apptEndTime: 'Appointment End Time',
  apptType: 'Appointment Type',
  unit: 'Units',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  city: 'City',
  state: 'State',
  country: 'Country',
  contact: 'Contact',
  phone: 'Phone',
  email: 'Email',
  name: 'Name',
};

/**
 * Section prefix mappings for form sections.
 * Used to add section context to field labels (e.g., "Customer Name" instead of just "Name").
 */
export const sectionPrefixMap: Record<string, string> = {
  customer: 'Customer -',
  billTo: 'Bill To -',
  pickup: 'Pickup -',
  consignee: 'Consignee -',
  specifications: 'Specs -',
  rateData: 'Rates -',
  operator: 'Operator -',
  carrier: 'Carrier -',
  accessorials: 'Accessorials -',
  notes: 'Notes -',
};

/**
 * TMS-specific field label overrides.
 * These can be used when a TMS has different terminology for the same field.
 */
const tmsSpecificLabelMaps: Record<string, Record<string, string>> = {
  [TMS.Turvo]: {
    ...baseFieldLabelMap,
    // Turvo-specific overrides can go here
  },
  [TMS.Revenova]: {
    ...baseFieldLabelMap,
    // Revenova-specific overrides can go here
  },
  [TMS.Aljex]: {
    ...baseFieldLabelMap,
    // Aljex-specific overrides can go here
  },
  [TMS.Mcleod]: {
    ...baseFieldLabelMap,
    // McLeod-specific overrides can go here
  },
  [TMS.McleodEnterprise]: {
    ...baseFieldLabelMap,
    // McLeod-specific overrides can go here
  },
  [TMS.ThreeG]: {
    ...baseFieldLabelMap,
    additionalReferences: 'Customer References',
    number: 'Reference Number',
    qualifierId: 'Reference Type',
    qualifier: 'Reference Type',
  },
  [TMS.Tai]: {
    ...baseFieldLabelMap,
    pickupDate: 'Pickup Ready',
    pickupCloseDate: 'Pickup Close',
    deliveryDate: 'Dropoff Estimated',
    deliveryCloseDate: 'Dropoff Close',
  },
};

export type FormatFieldLabelOptions = {
  // Total route stop count so Pickup / Stop N / Consignee labels resolve correctly.
  routeStopCount?: number;
};

function isMcleodTms(tmsName?: string): boolean {
  return tmsName === TMS.Mcleod || tmsName === TMS.McleodEnterprise;
}

function usesRouteStopLabels(tmsName?: string): boolean {
  return isMcleodTms(tmsName) || tmsName === TMS.PLS;
}

function routeStopFieldPrefix(index: number, routeStopCount?: number): string {
  if (index <= 0) {
    return 'Pickup';
  }

  if (routeStopCount != null && index >= routeStopCount - 1) {
    return 'Consignee';
  }

  return `Stop ${index}`;
}

/**
 * Formats a field path into a human-readable label for display in error messages.
 *
 * Handles special cases like:
 * - Array indices in customerLineItems: `rateData.customerLineItems.0.label` → "Rate Charge 1 Quantity"
 * - Section prefixes: `customer.externalTMSID` → "Customer Name"
 * - Known field mappings: `zipCode` → "Zip Code"
 * - Falls back to titleCase for unknown fields
 *
 * @param path - The field path from react-hook-form (e.g., "customer.externalTMSID")
 * @param tmsName - Optional TMS name for TMS-specific label overrides
 * @param options - Optional extras (e.g. route stop count for Pickup/Stop/Consignee labels)
 * @returns Human-readable label for the field
 *
 * @example
 * formatFieldLabel('customer.externalTMSID') // → "Customer Name"
 * formatFieldLabel('rateData.customerLineItems.0.label') // → "Rate Charge 1 Quantity"
 * formatFieldLabel('specifications.minTempFahrenheit') // → "Specs Min Temp Fahrenheit"
 */
export function formatFieldLabel(
  path: string,
  tmsName?: string,
  options?: FormatFieldLabelOptions
): string {
  // Special case: Customer charge items in rateData
  const customerLineItemMatch = path.match(
    /^rateData\.customerLineItems\.(\d+)\.(\w+)/
  );
  if (customerLineItemMatch) {
    const idx = customerLineItemMatch[1];
    const field = customerLineItemMatch[2];
    if (field === 'label') {
      return `Rate Charge ${parseInt(idx) + 1} Label`;
    }
    if (field === 'quantity') {
      return `Rate Charge ${parseInt(idx) + 1} Quantity`;
    }
    if (field === 'unitBasis') {
      return `Rate Charge ${parseInt(idx) + 1} Unit Basis`;
    }
    if (field === 'ratePerUnitUSD') {
      return `Rate Charge ${parseInt(idx) + 1} Rate`;
    }
    if (field === 'totalChargeUSD') {
      return `Rate Charge ${parseInt(idx) + 1} Total`;
    }
  }

  // Special case: Carrier charge items in rateData
  const carrierLineItemMatch = path.match(
    /^rateData\.carrierLineItems\.(\d+)\.(\w+)/
  );
  if (carrierLineItemMatch) {
    const idx = carrierLineItemMatch[1];
    const field = carrierLineItemMatch[2];
    if (field === 'label') {
      return `Carrier Charge ${parseInt(idx) + 1} Label`;
    }
    if (field === 'quantity') {
      return `Carrier Charge ${parseInt(idx) + 1} Quantity`;
    }
    // Add more as needed
  }

  // PLS reference fields are keyed by codes or card positions; the message names the field.
  if (
    path === 'plsOperationalFields' ||
    path.startsWith('plsOperationalFields.')
  ) {
    return 'Customer Reference Fields';
  }

  if (path.startsWith('plsPendingReferences.')) {
    return 'Load Reference Fields';
  }

  // Special case: Commodities array
  const commodityMatch = path.match(/^commodities\.(\d+)\.(\w+)/);
  if (commodityMatch) {
    const idx = commodityMatch[1];
    const field = commodityMatch[2];

    return `Commodity ${parseInt(idx) + 1} ${titleCase(field)}`;
  }

  // Quick Quote auto transport vehicles
  const autoVehicleMatch = path.match(/^autoTransport\.vehicles\.(\d+)\.(\w+)/);
  if (autoVehicleMatch) {
    const idx = autoVehicleMatch[1];
    const field = autoVehicleMatch[2];
    const fieldLabel =
      field === 'isInoperable' ? 'Condition' : titleCase(field);

    return `Vehicle ${parseInt(idx) + 1} ${fieldLabel}`;
  }

  if (path === 'autoTransport.trailerType') {
    return 'Trailer Type';
  }

  if (path === 'autoTransport.vehicles') {
    return 'Vehicles';
  }

  // Quick Quote LTL item lines
  const ltlItemMatch = path.match(/^ltl\.items\.(\d+)\.(\w+)/);
  if (ltlItemMatch) {
    const idx = ltlItemMatch[1];
    const field = ltlItemMatch[2];

    return `Commodity ${parseInt(idx) + 1} ${titleCase(field)}`;
  }

  // Special case: Stops array (multi-stop route forms)
  const stopMatch = path.match(/^stops\.(\d+)\.(?:.*\.)?(\w+)/);
  if (stopMatch) {
    const index = parseInt(stopMatch[1], 10);
    const field = stopMatch[2];
    const fieldLabel =
      isMcleodTms(tmsName) && field === 'refNumber'
        ? 'PO #'
        : baseFieldLabelMap[field] || titleCase(field);

    if (usesRouteStopLabels(tmsName)) {
      return `${routeStopFieldPrefix(index, options?.routeStopCount)} ${fieldLabel}`;
    }

    return `Stop ${index + 1} ${fieldLabel}`;
  }

  // Get the appropriate label map (TMS-specific or base)
  const labelMap = tmsName ? tmsSpecificLabelMaps[tmsName] : undefined;
  const replacements = labelMap || baseFieldLabelMap;

  // Split path and filter out numeric indices and 'val' properties
  const parts = path
    .split('.')
    .filter(
      (pathPart) => pathPart && !/^\d+$/.test(pathPart) && pathPart !== 'val'
    );

  // Build the formatted label
  const formattedLabel = parts
    .map((part, idx) => {
      // Apply section prefix for first part if it exists
      if (idx === 0 && sectionPrefixMap[part]) {
        return sectionPrefixMap[part];
      }
      // Apply field replacement if it exists
      if (replacements[part] != null) {
        return replacements[part];
      }
      // Fall back to titleCase
      return titleCase(part);
    })
    .filter(Boolean)
    .join(' ');

  return formattedLabel || titleCase(path);
}
